import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import API_URL from '../config';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import './Footer.css';
import { toEmailInput, emailInputProps } from '../utils/emailInput';

function Footer() {
  const { t, i18n } = useTranslation();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // idle | loading | success | error
  const [newsletterError, setNewsletterError] = useState('');

  // Zelfde redenering als Navbar.js: de footer bevat links naar andere
  // publieke marketingpagina's (Home, Aanbod, Over ons, ...) die in de
  // native app niet bestaan/niet de bedoeling zijn om naartoe te
  // navigeren (de app heeft enkel Splash/Login/Dashboard/Admin/Cleaner +
  // Privacy/Voorwaarden vanuit het profielmenu). Footer dus volledig
  // weglaten in de native app, ook op Privacy/Terms-pagina's.
  const isNative = localStorage.getItem('isNativeApp') === 'true';
  if (isNative) {
    return null;
  }

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (newsletterStatus === 'loading') return;
    if (!validateRequiredFields(e.target)) {
      setNewsletterStatus('error');
      setNewsletterError(t('common.fillRequired'));
      return;
    }
    setNewsletterStatus('loading');
    setNewsletterError('');
    try {
      await axios.post(`${API_URL}/api/newsletter`, { email: newsletterEmail, lang: i18n.language });
      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterStatus('error');
      setNewsletterError(error.response?.data?.error || t('footer.newsletterError'));
    }
  };

  return (
    <footer className="site-footer">
      <div className="footer-newsletter">
        <div className="footer-newsletter-text">
          <strong>{t('footer.newsletterTitle')}</strong>
          <span>{t('footer.newsletterSubtitle')}</span>
        </div>
        {newsletterStatus === 'success' ? (
          <p className="footer-newsletter-success">{t('footer.newsletterSuccess')}</p>
        ) : (
          <form className="footer-newsletter-form" onSubmit={handleNewsletterSubmit} noValidate onInput={clearFieldInvalid}>
            <input
              type="email"
              required
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(toEmailInput(e.target.value))}
              {...emailInputProps}
              placeholder={t('footer.newsletterPlaceholder')}
              aria-label={t('footer.newsletterPlaceholder')}
              className="footer-newsletter-input"
            />
            <button type="submit" className="footer-newsletter-btn" disabled={newsletterStatus === 'loading'}>
              {newsletterStatus === 'loading' ? t('common.loading') : t('footer.newsletterSubmit')}
            </button>
          </form>
        )}
        {newsletterStatus === 'error' && <p className="footer-newsletter-error">{newsletterError}</p>}
      </div>

      <div className="footer-top">
        <div className="footer-about">
          <p>&copy; 2026 SmartStays. {t('footer.rights')}</p>
          <p><a href="mailto:info@smartstays.be">info@smartstays.be</a></p>
        </div>

        <div className="footer-right">
          <nav className="footer-links">
            <Link to="/">{t('nav.home')}</Link>
            <Link to="/selection">{t('nav.pricing')}</Link>
            <Link to="/about">{t('nav.about')}</Link>
            <Link to="/contact">{t('nav.contact')}</Link>
            <Link to="/faq">{t('nav.faq')}</Link>
            <Link to="/download">{t('nav.download')}</Link>
            <Link to="/privacy">{t('footer.privacyLink')}</Link>
            <Link to="/terms">{t('footer.termsLink')}</Link>
          </nav>
          {/* Social-icons en review-badges samen in 1 groep i.p.v. 2 los
              gestapelde rijen - op telefoon staan ze zo naast/onder elkaar
              als de breedte toelaat i.p.v. altijd elk een eigen volle regel
              te nemen, wat de footer verticaal minder lang maakt en dichter
              bij de desktop-indeling (alles in 1 rij naast footer-links)
              aanleunt. */}
          <div className="footer-bottom-row">
            <div className="social-links">
              <a href="https://www.instagram.com/smartstaysbe/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61591161593047" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M13.5 21V13.2H15.8L16.2 10.4H13.5V8.6C13.5 7.8 13.7 7.2 14.9 7.2H16.3V4.7C16 4.7 15.1 4.6 14.1 4.6C11.9 4.6 10.4 5.9 10.4 8.3V10.4H8V13.2H10.4V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="https://www.tiktok.com/@smartstaysbe" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 3V14.8C14 16.35 12.75 17.6 11.2 17.6C9.65 17.6 8.4 16.35 8.4 14.8C8.4 13.25 9.65 12 11.2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 3C14 5.4 16.2 7.5 18.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/smartstaysbe/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                  <rect x="6.5" y="10" width="2.4" height="7.5" fill="currentColor"/>
                  <circle cx="7.7" cy="6.8" r="1.4" fill="currentColor"/>
                  <path d="M11.5 17.5V10M11.5 12.8C11.5 11.2 12.5 10 13.9 10C15.3 10 16.2 11 16.2 12.8V17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>

            {/* Eén gedeeld label boven de twee manieren om ons te
                beoordelen, i.p.v. elk knopje zijn eigen label te geven.
                Google-knop: eigen link naar onze Maps-vermelding (geen
                werkend Google Place ID beschikbaar voor de officiële
                "schrijf een review"-deeplink, vandaar de gewone Maps-link -
                gebruikers klikken daar zelf op "Recensie schrijven").
                Trustpilot-knop: zelfde zelf-gestylede link als voorheen
                (Trustpilot's officiële widget rendert altijd als een wit
                kadertje dat niet herstijlbaar is en hard afstak tegen de
                donkere footer). */}
            <div className="footer-reviews">
              <span className="footer-reviews-label">{t('footer.reviewsLabel')}</span>

              {/* Beide knopjes zitten samen in hun eigen rij (i.p.v. rechtstreeks
                  als flex-items van .footer-reviews) zodat ze altijd samen naast
                  elkaar blijven staan - het label erboven mag op zijn eigen regel
                  staan, maar Google/Trustpilot horen bij elkaar en wrappen dus
                  hoogstens SAMEN naar een volgende regel, nooit elk apart. */}
              <div className="footer-review-badges">
                <a
                  href="https://maps.app.goo.gl/yj6ZeNAS3H2KMV3B6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-google-badge"
                >
                  <svg className="footer-google-badge-icon" width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#4285F4" d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.82Z"/>
                    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.88-3c-1.08.72-2.46 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.27v3.1A12 12 0 0 0 12 24Z"/>
                    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.38l4-3.1Z"/>
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.23 0 12 0A12 12 0 0 0 1.27 6.62l4 3.1C6.22 6.86 8.87 4.75 12 4.75Z"/>
                  </svg>
                  <span className="footer-google-stars" aria-hidden="true">
                    <span className="footer-google-star">★</span>
                    <span className="footer-google-star">★</span>
                    <span className="footer-google-star">★</span>
                    <span className="footer-google-star">★</span>
                    <span className="footer-google-star">★</span>
                  </span>
                  <span className="footer-google-badge-name">Google</span>
                </a>

                <a
                  href="https://nl.trustpilot.com/review/smartstays.be"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-trustpilot-badge"
                >
                  <span className="footer-trustpilot-stars" aria-hidden="true">
                    <span className="footer-trustpilot-star">★</span>
                    <span className="footer-trustpilot-star">★</span>
                    <span className="footer-trustpilot-star">★</span>
                    <span className="footer-trustpilot-star">★</span>
                    <span className="footer-trustpilot-star">★</span>
                  </span>
                  <span className="footer-trustpilot-badge-name">Trustpilot</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
